import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/rn3";
import { useAuth } from "@/lib/auth";
import { Search, X, ArrowRight, Move, EyeOff, Eye } from "lucide-react";

const POSITION_KEY = "rn3.globalSearch.position";
const VALID_POSITIONS = ["top", "bottom", "hidden"];
const nextPosition = (p) => {
  const i = VALID_POSITIONS.indexOf(p);
  return VALID_POSITIONS[(i + 1) % VALID_POSITIONS.length];
};

/**
 * ⌘K global command palette — admin-only.
 * Hits `/api/search/global?q=…` and shows top hits across every collection
 * (books, accounts, transactions, statements, invoices, bills, customers,
 * vendors, leads, rules). Click or Enter to jump.
 */
const KIND_COLOR = {
  book: "text-[#8B1C22]",
  account: "text-[#166534]",
  transaction: "text-[#1E40AF]",
  statement: "text-[#78350F]",
  invoice: "text-[#5B21B6]",
  bill: "text-[#404040]",
  customer: "text-[#0A0A0A]",
  vendor: "text-[#0A0A0A]",
  lead: "text-[#8B1C22]",
  rule: "text-[#166534]",
};

const GlobalSearch = () => {
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ groups: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  // User-controllable placement: top-middle (default), bottom-right, or hidden.
  // Persisted to localStorage so every admin's preference survives reloads.
  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") return "top";
    const saved = window.localStorage?.getItem(POSITION_KEY);
    return VALID_POSITIONS.includes(saved) ? saved : "top";
  });
  const savePosition = (p) => {
    setPosition(p);
    try { window.localStorage.setItem(POSITION_KEY, p); } catch { /* SSR / private mode */ }
  };

  // Flat list for keyboard nav
  const flat = useMemo(() => {
    const rows = [];
    (data.groups || []).forEach((g) => {
      g.items.forEach((it) => rows.push({ ...it, kind: g.kind }));
    });
    return rows;
  }, [data]);

  // ⌘K / Ctrl+K to open, Esc to close
  useEffect(() => {
    if (!isAdmin) return;
    const onKey = (e) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      const key = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k";
      if (key) { e.preventDefault(); setOpen((o) => !o); }
      else if (e.key === "Escape" && open) { setOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAdmin, open]);

  // Auto-focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    if (!open) { setQ(""); setData({ groups: [], total: 0 }); setCursor(0); }
  }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (!query) { setData({ groups: [], total: 0 }); return; }
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search/global`, { params: { q: query } });
        setData(data);
        setCursor(0);
      } catch {
        // Silent fail — palette is optional
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(h);
  }, [q, open]);

  const go = (row) => {
    if (!row) return;
    setOpen(false);
    if (row.href) navigate(row.href);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, Math.max(0, flat.length - 1))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); go(flat[cursor]); }
  };

  if (!isAdmin) return null;

  // Position classes for the trigger pill. Kept as a static map so
  // Tailwind's JIT compiles every variant (no dynamic class concat).
  // NOTE: sticky site Header is z-50 top-0, so top-position must sit above it
  // (z-[60]) and land just under the header line to stay visible.
  const positionClasses = {
    top: "fixed top-20 left-1/2 -translate-x-1/2 z-[60]",
    bottom: "fixed bottom-24 right-4 z-[60]",
    hidden: "fixed bottom-4 right-4 z-[60] opacity-60 hover:opacity-100",
  };

  const TriggerBar = () => {
    if (position === "hidden") {
      // Minimal recovery affordance — a tiny corner chip so an accountant
      // who accidentally hid the bar (or clients handed a shared machine)
      // can always bring it back. ⌘K keyboard shortcut still works too.
      return (
        <button
          onClick={() => savePosition("top")}
          className={`${positionClasses.hidden} rn3-btn-gold text-[10px] uppercase tracking-widest inline-flex items-center gap-1 shadow`}
          title="Show search bar (⌘K still works)"
          data-testid="global-search-restore-btn"
        >
          <Eye className="w-3 h-3" />
          <kbd className="text-[9px] border border-[#8B1C22] px-1 py-0.5 rounded bg-white text-[#8B1C22]">⌘K</kbd>
        </button>
      );
    }
    return (
      <div className={`${positionClasses[position]} flex items-center gap-1`}>
        <button
          onClick={() => setOpen(true)}
          className="rn3-btn-gold text-[10px] uppercase tracking-widest inline-flex items-center gap-2 shadow-lg px-4 py-2"
          title="Search (⌘K)"
          data-testid="global-search-btn"
        >
          <Search className="w-3.5 h-3.5" />
          <span>Search</span>
          <kbd className="ml-1 text-[9px] border border-[#8B1C22] px-1 py-0.5 rounded bg-white text-[#8B1C22]">⌘K</kbd>
        </button>
        {/* Move / Hide toolbar — always visible so anyone can reposition */}
        <button
          onClick={() => savePosition(position === "top" ? "bottom" : "top")}
          className="text-[#737373] hover:text-[#8B1C22] bg-white/80 backdrop-blur border border-[#E5E5E5] shadow-sm p-1"
          title={position === "top" ? "Move to bottom-right" : "Move to top-center"}
          data-testid="global-search-move-btn"
        >
          <Move className="w-3 h-3" />
        </button>
        <button
          onClick={() => savePosition("hidden")}
          className="text-[#737373] hover:text-[#8B1C22] bg-white/80 backdrop-blur border border-[#E5E5E5] shadow-sm p-1"
          title="Hide search bar (⌘K keyboard shortcut still works)"
          data-testid="global-search-hide-btn"
        >
          <EyeOff className="w-3 h-3" />
        </button>
      </div>
    );
  };

  return (
    <>
      <TriggerBar />
      {open && (
        <div
          className="fixed inset-0 z-[9998] bg-black/40 flex items-start justify-center pt-[10vh] px-4"
          onClick={() => setOpen(false)}
          data-testid="global-search-overlay"
        >
          <div
            className="bg-white w-full max-w-2xl border border-[#C4A252] shadow-2xl flex flex-col max-h-[70vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-[#E5E5E5] px-3 py-2">
              <Search className="w-4 h-4 text-[#8B1C22] flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search everything — books, accounts, transactions, statements, invoices, customers, vendors, leads…"
                className="flex-1 text-sm py-1 focus:outline-none"
                data-testid="global-search-input"
              />
              {loading && <span className="text-[10px] text-[#C4A252] animate-pulse">…</span>}
              <button
                onClick={() => setOpen(false)}
                className="text-[#737373] hover:text-[#8B1C22]"
                data-testid="global-search-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1" data-testid="global-search-results">
              {!q.trim() ? (
                <div className="p-6 text-center text-xs text-[#737373]">
                  Type any transaction, vendor, invoice number, amount ($1,234.56), date, or account code.
                  <div className="mt-2 text-[10px]">
                    <kbd className="border border-[#E5E5E5] px-1 py-0.5 rounded">↑</kbd>
                    <kbd className="border border-[#E5E5E5] px-1 py-0.5 rounded ml-1">↓</kbd> move ·
                    <kbd className="border border-[#E5E5E5] px-1 py-0.5 rounded ml-1">Enter</kbd> pick ·
                    <kbd className="border border-[#E5E5E5] px-1 py-0.5 rounded ml-1">Esc</kbd> close
                  </div>
                </div>
              ) : data.total === 0 && !loading ? (
                <div className="p-6 text-center text-xs text-[#737373]" data-testid="global-search-empty">
                  No matches for <b>{q}</b>
                </div>
              ) : (
                (() => {
                  let idx = -1;
                  return (data.groups || []).map((g) => (
                    <div key={g.kind}>
                      <div className="px-3 py-1 bg-[#FDFBF7] border-y border-[#F5F5F5]">
                        <span className={`text-[10px] uppercase tracking-widest font-medium ${KIND_COLOR[g.kind] || "text-[#404040]"}`}>
                          {g.label}
                        </span>
                        <span className="text-[10px] text-[#737373] ml-2">{g.items.length}</span>
                      </div>
                      {g.items.map((it) => {
                        idx += 1;
                        const active = idx === cursor;
                        return (
                          <button
                            key={`${g.kind}-${it.id}`}
                            onClick={() => go({ ...it, kind: g.kind })}
                            onMouseEnter={() => setCursor(idx)}
                            className={`w-full text-left px-3 py-2 flex items-center gap-2 border-b border-[#F5F5F5] ${active ? "bg-[#FDFBF7]" : "bg-white"} hover:bg-[#FDFBF7]`}
                            data-testid={`global-search-item-${g.kind}-${it.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-medium truncate ${KIND_COLOR[g.kind] || "text-[#0A0A0A]"}`}>
                                {it.title}
                              </div>
                              {it.subtitle && (
                                <div className="text-[10px] text-[#737373] truncate">{it.subtitle}</div>
                              )}
                            </div>
                            <ArrowRight className="w-3 h-3 text-[#C4A252] flex-shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  ));
                })()
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
